/**
 * PB-FOUNDATION finalize helpers — cart encode, session overlay, field flush.
 * PB-FND-32: extracted from pb-logic-core.liquid.
 */

import {
  flushBuilderState,
  encodeTier1Properties,
  resolveMainAiImageByLabel,
  resolveMainAiTextByLabel,
} from '@theme/pb-flush-encode';
import { buildAiTextDraftFromCartProps } from '@theme/pb-dl-ai-hydrate';
import { stampCartFamily } from '@theme/pb-cart-family-sync';

/**
 * @param {object} deps
 * @param {object} deps.pb — productBuilder instance
 */
export function createFinalizeHelpers(deps) {
  const pb = deps.pb || deps;

  function buildItemProperties(selections, options) {
    if (typeof pb.buildItemPropertiesV2 === 'function') {
      const serializeMode = pb.CART_SERIALIZE_MODE || 'numbered';
      return pb.buildItemPropertiesV2(selections, options, serializeMode);
    }
    const props = {};
    const skip = new Set();
    (options || []).forEach((opt) => {
      if (opt && opt.name) skip.add(opt.name);
    });
    skip.add('Title');
    Object.keys(selections || {}).forEach((key) => {
      if (skip.has(key)) return;
      const val = selections[key];
      if (val == null || val === '') return;
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        Object.keys(val).forEach((subKey) => {
          const subVal = val[subKey];
          if (subVal != null && subVal !== '') {
            props[subKey] = Array.isArray(subVal) ? subVal.join('|||') : String(subVal);
          }
        });
      } else {
        props[key] = Array.isArray(val) ? val.join('|||') : val;
      }
    });
    return props;
  }

  function buildBundleEffectiveMainProps(selections, options) {
    if (typeof pb.buildBundleEffectiveMainPropsV2 === 'function') {
      const serializeMode = pb.CART_SERIALIZE_MODE || 'numbered';
      return pb.buildBundleEffectiveMainPropsV2(selections, options, serializeMode);
    }
    const props = {};
    Object.keys(selections || {}).forEach((key) => {
      if (key === 'Title') return;
      const val = selections[key];
      if (val == null || val === '') return;
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        Object.keys(val).forEach((subKey) => {
          const subVal = val[subKey];
          if (subVal != null && subVal !== '') {
            props[subKey] = Array.isArray(subVal) ? subVal.join('|||') : String(subVal);
          }
        });
      } else {
        props[key] = Array.isArray(val) ? val.join('|||') : val;
      }
    });
    return props;
  }

  function expandDelimiterProperties(props) {
    if (typeof pb.expandPropertiesV2 === 'function') {
      return pb.expandPropertiesV2(props);
    }
    if (!props || typeof props !== 'object') return props || {};
    const out = {};
    Object.keys(props).forEach((key) => {
      const val = props[key];
      if (val == null || val === '') return;
      if (typeof val === 'string' && val.indexOf('|||') !== -1) {
        out[key] = val.split('|||').map((s) => s.trim()).filter(Boolean);
      } else {
        out[key] = val;
      }
    });
    return out;
  }

  function collapseNumberedProperties(props) {
    if (typeof pb.collapsePropertiesV2 === 'function') {
      return pb.collapsePropertiesV2(props);
    }
    if (!props || typeof props !== 'object') return props || {};
    const numbered = {};
    const rest = {};
    const numSuffix = /^(.+?)\s*\(\s*(\d+)\s*\)$/;
    Object.keys(props).forEach((key) => {
      const m = String(key).match(numSuffix);
      if (m) {
        const base = m[1].trim();
        const idx = parseInt(m[2], 10);
        if (!numbered[base]) numbered[base] = [];
        numbered[base][idx - 1] = props[key];
      } else {
        rest[key] = props[key];
      }
    });
    Object.keys(numbered).forEach((base) => {
      const arr = numbered[base].filter(Boolean);
      if (arr.length) rest[base] = arr;
    });
    return rest;
  }

  function getPersonalizationFields(product) {
    const item = product || (typeof window !== 'undefined' ? window.BUILDER_DATA?.mainProduct : null);
    if (!item) return [];
    if (Array.isArray(item.personalization) && item.personalization.length) {
      return flattenPersonalizationFields(item.personalization);
    }
    if (Array.isArray(item.config) && item.config.length) return item.config;
    return [];
  }

  function flattenPersonalizationFields(source) {
    if (!source || !Array.isArray(source)) return [];
    if (source.length && source[0] && (source[0].fields || source[0].fields?.value)) {
      const out = [];
      source.forEach((set) => {
        const fields = set.fields?.value || set.fields || [];
        (Array.isArray(fields) ? fields : []).forEach((f) => {
          if (f) out.push(f);
        });
      });
      return out;
    }
    return source;
  }

  function parseTopicSelectionsBackup(props) {
    if (!props || !props._pb_json) return null;
    try {
      const backup = JSON.parse(props._pb_json);
      if (backup?.topicSelections && typeof backup.topicSelections === 'object') return backup.topicSelections;
    } catch (e) {}
    return null;
  }

  function mergeAiImageSelectionsForEncode(product, rawSelections) {
    const source = rawSelections && typeof rawSelections === 'object' ? rawSelections : {};
    let selections = { ...source };
    if (product && typeof pb.flattenAiImageSelectionsForProps === 'function') {
      selections = pb.flattenAiImageSelectionsForProps(product, source);
    }
    if (product && typeof pb.extractAiImagePropsFromSelections === 'function') {
      const aiFlat = pb.extractAiImagePropsFromSelections(product, source);
      Object.keys(aiFlat || {}).forEach((key) => {
        if (aiFlat[key] != null && aiFlat[key] !== '') selections[key] = aiFlat[key];
      });
    }
    return selections;
  }

  function maybeAttachMainAiImageByLabel(stateSlice, meta) {
    if (!stateSlice || stateSlice.aiImageByLabel || (meta && meta.skipMainAiImageCollect)) {
      return stateSlice;
    }
    const product =
      (meta && meta.fieldItem) ||
      (typeof window !== 'undefined' ? window.BUILDER_DATA?.mainProduct : null);
    const mainProduct =
      typeof window !== 'undefined' ? window.BUILDER_DATA?.mainProduct : null;
    if (!product || !mainProduct || String(product.id) !== String(mainProduct.id)) {
      return stateSlice;
    }
    if (
      meta &&
      meta.bundleRowIndex != null &&
      meta.firstMainIdx != null &&
      meta.bundleRowIndex !== meta.firstMainIdx
    ) {
      return stateSlice;
    }
    const builderState = typeof window !== 'undefined' ? window.builderState : null;
    const containerSelector =
      builderState?.mode === 'SUB' ? '#sub-flow-container' : '#main-flow-container';
    const aiImageByLabel = resolveMainAiImageByLabel(containerSelector, { builderState });
    if (!aiImageByLabel) return stateSlice;
    return { ...stateSlice, aiImageByLabel };
  }

  function maybeAttachMainAiTextByLabel(stateSlice, meta) {
    if (!stateSlice || stateSlice.aiTextByLabel || (meta && meta.skipMainAiTextCollect)) {
      return stateSlice;
    }
    const product =
      (meta && meta.fieldItem) ||
      (typeof window !== 'undefined' ? window.BUILDER_DATA?.mainProduct : null);
    const mainProduct =
      typeof window !== 'undefined' ? window.BUILDER_DATA?.mainProduct : null;
    if (!product || !mainProduct || String(product.id) !== String(mainProduct.id)) {
      return stateSlice;
    }
    if (
      meta &&
      meta.bundleRowIndex != null &&
      meta.firstMainIdx != null &&
      meta.bundleRowIndex !== meta.firstMainIdx
    ) {
      return stateSlice;
    }
    const builderState = typeof window !== 'undefined' ? window.builderState : null;
    const containerSelector =
      builderState?.mode === 'SUB' ? '#sub-flow-container' : '#main-flow-container';
    const aiTextByLabel = resolveMainAiTextByLabel(containerSelector, { builderState });
    if (!aiTextByLabel) return stateSlice;
    return { ...stateSlice, aiTextByLabel };
  }

  function encodeCartLineProperties(stateSlice, options, extraProps, meta) {
    const extra = extraProps || {};
    const product = (meta && meta.fieldItem) || (typeof window !== 'undefined' ? window.BUILDER_DATA?.mainProduct : null);
    const sliceWithAi = maybeAttachMainAiTextByLabel(
      maybeAttachMainAiImageByLabel(stateSlice, meta),
      meta
    );
    return encodeTier1Properties(
      {
        stateSlice: sliceWithAi,
        product,
        fieldItem: meta && meta.fieldItem,
        options,
        extraProps: extra,
      },
      { pb, getPersonalizationFields }
    );
  }

  function encodeCartLineFromStoredProps(storedProps, options, meta) {
    let expanded = storedProps || {};
    if (typeof pb.expandPropertiesV2 === 'function') {
      expanded = pb.expandPropertiesV2(storedProps || {}, { ignoreHiddenJson: true });
    }
    let topics = (meta && meta.topicSelections) || null;
    if (!topics) {
      topics = parseTopicSelectionsBackup(storedProps || {}) || {};
    }
    let catalogDesignLibrary = meta && meta.catalogDesignLibrary;
    if (typeof pb.decodeCartLineToBuilderState === 'function') {
      const fields = meta && meta.fieldItem ? getPersonalizationFields(meta.fieldItem) : [];
      const decoded = pb.decodeCartLineToBuilderState({ properties: storedProps || {} }, fields, {
        options: options || [],
      });
      if (decoded && decoded.catalogDesignLibrary) {
        catalogDesignLibrary = decoded.catalogDesignLibrary;
        if (typeof pb.scopeCatalogDesignLibraryForFields === 'function') {
          catalogDesignLibrary = pb.scopeCatalogDesignLibraryForFields(catalogDesignLibrary, fields);
        }
      }
      if (decoded && decoded.selections && typeof decoded.selections === 'object') {
        Object.keys(decoded.selections).forEach((k) => {
          if (expanded[k] == null || expanded[k] === '') {
            expanded[k] = decoded.selections[k];
          }
        });
      }
      if ((!topics || !Object.keys(topics).length) && decoded && decoded.topicSelections) {
        topics = decoded.topicSelections;
      }
    }
    if (meta && meta.fieldItem && typeof pb.filterSelectionsForProductFields === 'function') {
      expanded = pb.filterSelectionsForProductFields(
        expanded,
        getPersonalizationFields(meta.fieldItem),
        options || meta.fieldItem.options || []
      );
    }
    return encodeCartLineProperties(
      {
        selections: expanded,
        aiTextByLabel: buildAiTextDraftFromCartProps(
          storedProps, getPersonalizationFields(meta?.fieldItem), expanded
        ) || { byLabel: {} },
        topicSelections: topics || {},
        catalogDesignLibrary: catalogDesignLibrary,
        variantId: meta && meta.variantId,
      },
      options,
      (meta && meta.extra) || {},
      meta
    );
  }

  function buildCartLineSessionOverlay(selections, topicSelections, catalogDesignLibrary) {
    const overlay = {
      mode: 'MAIN',
      main: {
        selections: selections && typeof selections === 'object' ? { ...selections } : {},
        topicSelections: topicSelections && typeof topicSelections === 'object' ? { ...topicSelections } : {},
        touched: {},
      },
    };
    if (catalogDesignLibrary && catalogDesignLibrary.byLabel) {
      overlay.catalogDesignLibrary = { byLabel: { ...catalogDesignLibrary.byLabel } };
    }
    return overlay;
  }

  function flushFlowFieldsToState(containerSelector) {
    flushBuilderState({ containerSelector }, { pb });
  }

  function encodeFlowStateForCart(stateSlice, product, options) {
    return encodeTier1Properties(
      {
        stateSlice,
        product,
        options: options || [],
      },
      { pb, getPersonalizationFields }
    );
  }

  return {
    stampCartFamily,
    buildItemProperties,
    buildBundleEffectiveMainProps,
    expandDelimiterProperties,
    collapseNumberedProperties,
    encodeCartLineProperties,
    encodeCartLineFromStoredProps,
    buildCartLineSessionOverlay,
    parseTopicSelectionsBackup,
    flattenPersonalizationFields,
    flushFlowFieldsToState,
    encodeFlowStateForCart,
    getPersonalizationFields,
  };
}

const FINALIZE_METHODS = [
  'stampCartFamily',
  'buildItemProperties',
  'buildBundleEffectiveMainProps',
  'expandDelimiterProperties',
  'collapseNumberedProperties',
  'encodeCartLineProperties',
  'encodeCartLineFromStoredProps',
  'buildCartLineSessionOverlay',
  'parseTopicSelectionsBackup',
  'flattenPersonalizationFields',
  'flushFlowFieldsToState',
  'encodeFlowStateForCart',
  'getPersonalizationFields',
];

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBFinalize = { createFinalizeHelpers };
}

function install(pb) {
  if (!pb || !window.PBFinalize) return false;
  if (pb._pbFinalizeInstalled) return true;
  const finalize = createFinalizeHelpers({ pb });
  pb._pbFinalizeInstalled = true;
  pb._pbFinalize = finalize;
  FINALIZE_METHODS.forEach((name) => {
    pb[name] = function (...args) {
      return finalize[name](...args);
    };
  });
  return true;
}

function tryInstall() {
  if (typeof window === 'undefined' || !window.productBuilder) return false;
  return install(window.productBuilder);
}

attachToWindow();

if (typeof window !== 'undefined') {
  window.__pbEnsureFinalize = install;
  if (!tryInstall()) {
    document.addEventListener('DOMContentLoaded', tryInstall);
    document.addEventListener('builder:ready', tryInstall);
  }
}
